import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import AddIcon from "@material-ui/icons/Add";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import {
  FIRM_TYPES,
  firmTypeLabel,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import {
  ALL_FIRMS_TAB,
  AUDIT_TAB,
  fetchTabCounts,
} from "./ProfessionalContacts.helper";
import RepositoryList from "./RepositoryList";
import FirmRecord from "./FirmRecord";
import FirmForm from "./FirmForm";
import BulkUpload from "./BulkUpload";
import AuditLogView from "./AuditLogView";
import useStyles from "./ProfessionalContacts.styles";

const ProfessionalContacts = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(ALL_FIRMS_TAB);

  const [subView, setSubView] = useState(null);
  const [selectedFirmId, setSelectedFirmId] = useState(null);
  const [editFirmId, setEditFirmId] = useState(null);
  const [editOptions, setEditOptions] = useState({});
  const [counts, setCounts] = useState({});

  const refreshCounts = useCallback(() => {
    fetchTabCounts()
      .then(setCounts)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const openRecord = (firmId) => {
    setSelectedFirmId(firmId);
    setSubView("record");
  };

  const openAdd = () => {
    setEditFirmId(null);
    setSubView("add");
  };

  const openEdit = (firmId, options) => {
    setEditFirmId(firmId);
    setEditOptions(options || {});
    setSubView("add");
  };

  const openBulk = () => setSubView("bulk");

  const backToTab = (tab) => {
    if (tab) setActiveTab(tab);
    setSubView(null);
    setSelectedFirmId(null);
    setEditFirmId(null);
    refreshCounts();
  };

  const tabs = [
    [ALL_FIRMS_TAB, "All Firms", counts[ALL_FIRMS_TAB]],
    ...FIRM_TYPES.map((type) => [type, firmTypeLabel(type), counts[type]]),
    [AUDIT_TAB, "Audit Log", undefined],
  ];

  const isListView = subView === null;
  const activeTabIndex = Math.max(
    0,
    tabs.findIndex(([key]) => key === activeTab)
  );

  return (
    <Paper className={classes.surface} elevation={0}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant="h6" component="h2" className={classes.title}>
            Professional Contacts
          </Typography>
          <div className={classes.subtle}>
            Central repository shared across all loans.
          </div>
        </div>
        {isListView && (
          <div className={classes.headerActions}>
            <A_SecuredAction permission={pageSecurityActions.CREATE}>
              <A_Button
                label="Bulk Upload"
                size="small"
                premiumVariant="secondary"
                startIcon={<CloudUploadIcon fontSize="small" />}
                onClick={openBulk}
              />
            </A_SecuredAction>
            <A_SecuredAction permission={pageSecurityActions.CREATE}>
              <A_Button
                label="Add Company"
                size="small"
                premiumVariant="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openAdd}
              />
            </A_SecuredAction>
          </div>
        )}
      </div>

      <div className={classes.content}>
        {isListView && (
          <div className={classes.tabsBar}>
            <Tabs
              value={activeTabIndex}
              onChange={(event, index) => backToTab(tabs[index][0])}
              variant="scrollable"
              scrollButtons="auto"
              textColor="primary"
              indicatorColor="primary"
              className={classes.tabsRoot}
              aria-label="Professional Contacts sections"
            >
              {tabs.map(([key, label, count]) => (
                <Tab
                  key={key}
                  className={classes.tabRoot}
                  label={
                    <>
                      {label}
                      {count !== undefined && (
                        <span className={classes.tabCount}>{count}</span>
                      )}
                    </>
                  }
                />
              ))}
            </Tabs>
          </div>
        )}

        {subView === "record" && (
          <FirmRecord
            firmId={selectedFirmId}
            onBack={() => backToTab(activeTab)}
            onEdit={(firmId, options) => openEdit(firmId, options)}
            onDeleted={() => backToTab(activeTab)}
          />
        )}
        {subView === "add" && (
          <FirmForm
            firmId={editFirmId}
            defaultType={FIRM_TYPES.includes(activeTab) ? activeTab : undefined}
            initialAppend={editOptions}
            onCancel={() =>
              editFirmId ? openRecord(editFirmId) : backToTab(activeTab)
            }
            onSaved={(firm) => backToTab(firm.type)}
          />
        )}
        {subView === "bulk" && (
          <BulkUpload onBack={() => backToTab(activeTab)} />
        )}
        {isListView && activeTab === AUDIT_TAB && <AuditLogView />}
        {isListView && activeTab !== AUDIT_TAB && (
          <RepositoryList tab={activeTab} onOpenFirm={openRecord} />
        )}
      </div>
    </Paper>
  );
};

ProfessionalContacts.propTypes = {
  content: PropTypes.any,
};

export default ProfessionalContacts;
